import {
  Group,
  GroupResponse,
  OrganisationResponse,
  PractitionerData,
  useGetAllGroups,
  useGetOrganisation,
} from 'api';
import useGetPractitionerGroupsAndOrgs from 'components/hooks/useGetPractitionerGroupsAndOrgs';
import { useAuth } from 'contexts/AuthContext';
import React, { useContext, useMemo, useReducer } from 'react';
import { InviteUserOrgOptions } from 'types/types';
import { createGroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';
import getInviteEligibility, {
  InviteEligibility,
} from 'utils/getInviteEligibility';
import { getUniqueObjectsById } from 'utils/mappers/entityMappers';
import { isAdvancedUser, isGroupManager, isSuperAdmin } from 'utils/role';
import { PermissionRole } from './AddUserForm/types';

export type FormState = {
  permissionRole: PermissionRole;
  selectedGroup: GroupResponse | null;
  selectedOrgs: OrganisationResponse[];
  orgType: InviteUserOrgOptions;
  currentOrg: OrganisationResponse | undefined;
  existingPractitioner: (PractitionerData & { errors?: any }) | null;
};

export type InviteState = {
  practitionerOrganisations: (OrganisationResponse | GroupResponse)[];
  rawPractitionerOrganisations: OrganisationResponse[];
  practitionerGroupCount?: number;
  practitionerOrgCount?: number;
  currentOrganisation?: OrganisationResponse;
  groups: GroupResponse[];
  inviteEligibility: InviteEligibility | undefined;
  practitionerOrgsInGroups: Record<string, Group>;
  organisationsInGroups: Record<string, Group>;
  isPending: boolean;
  formState: FormState;
  setFormState: React.Dispatch<Partial<FormState>>;
};

const InviteContext = React.createContext<InviteState | undefined>(undefined);

export const useInviteContext = () => {
  const context = useContext(InviteContext);

  if (context === undefined) {
    throw Error(
      'context was undefined. Please ensure useInviteContext is used within a InviteProvider.',
    );
  }

  return context;
};

export const InviteProvider = ({
  children,
  currentOrgId,
  overwrite,
}: {
  children: React.ReactNode;
  currentOrgId?: string;
  overwrite?: Partial<InviteState>;
}) => {
  const { user } = useAuth();

  const [formState, setFormState] = useReducer(
    (data: FormState, partialData: Partial<FormState>) => ({
      ...data,
      ...partialData,
    }),
    {
      permissionRole: 'STANDARD',
      selectedGroup: null,
      selectedOrgs: [],
      currentOrg: undefined,
      orgType: InviteUserOrgOptions.ONE_ORG,
      existingPractitioner: null,
    },
  );

  const hasReadOrgGroupAccess =
    !!user?.permissions.Organisation_Group_ReadAll &&
    user.permissions.Organisation_Group_ReadAll !== 'None';
  const { data: rawGroupsData } = useGetAllGroups({
    enabled: hasReadOrgGroupAccess,
  });
  const rawGroups = useMemo(() => rawGroupsData ?? [], [rawGroupsData]);
  const organisationsInGroups = useMemo(
    () => createGroupOrganisationHashMap(rawGroups),
    [rawGroups],
  );
  const { data: currentOrganisation, isLoading: currentOrganisationLoading } =
    useGetOrganisation(currentOrgId);

  const {
    groupCount: practitionerGroupCount,
    orgCount: practitionerOrgCount,
    organisations: rawPractitionerOrganisations,
    orgsAndGroups: practitionerOrganisations,
    organisationInGroups: practitionerOrgsInGroups,
    isPending: practitionerPending,
  } = useGetPractitionerGroupsAndOrgs(formState.existingPractitioner, {
    enabled: !!formState.existingPractitioner,
  });

  const groups = useMemo(() => {
    if (!isSuperAdmin(user?.role)) {
      return rawGroups.filter(({ type }) => type === 'CUSTOMER');
    }

    return rawGroups;
  }, [user?.role, rawGroups]);

  const auGroups = useMemo(() => {
    if (!isAdvancedUser(user?.role) && !isGroupManager(user?.role)) {
      return undefined;
    }
    const inviteeOrgIds = rawPractitionerOrganisations?.map(({ id }) => id);

    const entries = Object.entries(organisationsInGroups).filter(([orgId]) =>
      inviteeOrgIds?.includes(orgId),
    );

    return (
      getUniqueObjectsById(entries.map(([, group]) => group)).filter(
        ({ organisations }) =>
          !organisations.every(({ id }: OrganisationResponse) =>
            inviteeOrgIds?.includes(id),
          ),
      ) ?? []
    );
  }, [rawPractitionerOrganisations, user, organisationsInGroups]);

  const inviteEligibility = useMemo(() => {
    const isSelectedOrganisationUnknown =
      !!currentOrgId && !currentOrganisation;
    if (isSelectedOrganisationUnknown) {
      return undefined;
    }
    return getInviteEligibility({
      inviter: user,
      invitee: formState.existingPractitioner ?? undefined,
      inviteeOrganisations: rawPractitionerOrganisations,
      selectedOrganisation: currentOrganisation,
      selectedOrganisationGroup: currentOrgId
        ? organisationsInGroups[currentOrgId]
        : undefined,
    });
  }, [
    user,
    formState.existingPractitioner,
    rawPractitionerOrganisations,
    currentOrgId,
    currentOrganisation,
    organisationsInGroups,
  ]);
  const isPending = practitionerPending || currentOrganisationLoading;

  const value: InviteState = useMemo(
    () => ({
      inviteEligibility,
      rawPractitionerOrganisations,
      practitionerGroupCount,
      practitionerOrgCount,
      practitionerOrganisations,
      practitionerOrgsInGroups,
      organisationsInGroups,
      currentOrganisation,
      groups: auGroups ?? groups,
      isPending,
      formState,
      setFormState,
      ...overwrite,
    }),
    [
      overwrite,
      inviteEligibility,
      practitionerOrganisations,
      rawPractitionerOrganisations,
      practitionerGroupCount,
      practitionerOrgCount,
      practitionerOrgsInGroups,
      organisationsInGroups,
      currentOrganisation,
      groups,
      isPending,
      auGroups,
      formState,
      setFormState,
    ],
  );

  return (
    <InviteContext.Provider value={value}>{children}</InviteContext.Provider>
  );
};
